// frontend/src/components/widgets/StreamingWidgetConfig.js - FIXED TRANSLATIONS
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Video, Sparkles, Youtube, Twitch as TwitchIcon } from 'lucide-react';

const StreamingWidgetConfig = ({ existingConfig, onSave, onCancel }) => {
  const { t } = useTranslation();
  
  // ✅ PRESET DEFINITIONS
  const STREAMING_PRESETS = {
    youtubeBasic: {
      platform: 'youtube',
      autoplay: true,
      muted: false,
      showChat: false,
      fallbackBehavior: 'skip',
      checkInterval: 300,
      theme: 'dark'
    },
    twitchFull: {
      platform: 'twitch',
      autoplay: true,
      muted: false,
      showChat: true,
      fallbackBehavior: 'show_message',
      checkInterval: 300,
      theme: 'purple'
    },
    gamingSetup: {
      platform: 'twitch',
      autoplay: true,
      muted: false,
      showChat: true,
      fallbackBehavior: 'show_logo',
      checkInterval: 180,
      theme: 'gaming'
    }
  };
  
  const [formData, setFormData] = useState({
    name: existingConfig?.name || '',
    platform: existingConfig?.config?.platform || 'youtube',
    channelId: existingConfig?.config?.channelId || '',
    autoplay: existingConfig?.config?.autoplay !== undefined ? existingConfig.config.autoplay : true,
    muted: existingConfig?.config?.muted || false,
    showChat: existingConfig?.config?.showChat || false,
    fallbackBehavior: existingConfig?.config?.fallbackBehavior || 'skip',
    checkInterval: existingConfig?.config?.checkInterval || 300,
    theme: existingConfig?.config?.theme || 'dark'
  });

  const [selectedPreset, setSelectedPreset] = useState('custom');
  const [errors, setErrors] = useState({});

  // ✅ PRESET HANDLER
  const handlePresetChange = (presetKey) => {
    if (presetKey === 'custom') {
      setSelectedPreset('custom');
      return;
    }

    const preset = STREAMING_PRESETS[presetKey];
    if (preset) {
      setFormData(prev => ({
        ...prev,
        ...preset
      }));
      setSelectedPreset(presetKey);
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = t('common.required');
    }
    
    if (!formData.channelId || formData.channelId.trim() === '') {
      newErrors.channelId = t('common.required');
    }
    
    if (formData.checkInterval < 60) {
      newErrors.checkInterval = 'Minimum 60 seconds';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    const config = {
      name: formData.name,
      config: {
        platform: formData.platform,
        channelId: formData.channelId,
        autoplay: formData.autoplay,
        muted: formData.muted,
        showChat: formData.showChat,
        fallbackBehavior: formData.fallbackBehavior,
        checkInterval: parseInt(formData.checkInterval),
        theme: formData.theme
      }
    };

    onSave(config);
  };

  // ✅ PRESET OPTIONS - FIXED TRANSLATION KEYS
  const presetOptions = [
    { value: 'custom', label: t('widgets.presetCustom') || 'Benutzerdefiniert' },
    { value: 'youtubeBasic', label: t('widgets.presetYouTube') || 'YouTube Basis', desc: 'Simple YouTube stream' },
    { value: 'twitchFull', label: t('widgets.presetTwitchFull') || 'Twitch Komplett', desc: 'Stream + Chat' },
    { value: 'gamingSetup', label: t('widgets.presetGaming') || 'Gaming Setup', desc: 'Gaming optimized' }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-3 rounded-lg">
            <Video className="text-white" size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('widgets.configTitle') || 'Streaming Widget konfigurieren'}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              YouTube/Twitch Live Streaming
            </p>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      {/* Content */}
      <div className="p-6 max-h-[70vh] overflow-y-auto space-y-6">
        
        {/* Widget Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.widgetName')} *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
            placeholder={t('widgets.widgetNamePlaceholder') || 'z.B. Lobby Stream Display'}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name}</p>
          )}
        </div>

        {/* ✅ PRESET SELECTOR - FIXED */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Sparkles size={20} />
            {t('widgets.presets') || 'Vorlagen'}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {presetOptions.map(preset => (
              <button
                key={preset.value}
                type="button"
                onClick={() => handlePresetChange(preset.value)}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  selectedPreset === preset.value
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700'
                }`}
              >
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {preset.label}
                </div>
                {preset.desc && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {preset.desc}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Platform Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.platform')} *
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => {
                setFormData({ ...formData, platform: 'youtube' });
                setSelectedPreset('custom');
              }}
              className={`p-4 border-2 rounded-lg transition-all ${
                formData.platform === 'youtube'
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-red-300'
              }`}
            >
              <div className="text-center">
                <div className="text-3xl mb-2">▶️</div>
                <div className="font-semibold">{t('widgets.youtube') || 'YouTube'}</div>
              </div>
            </button>
            
            <button
              type="button"
              onClick={() => {
                setFormData({ ...formData, platform: 'twitch' });
                setSelectedPreset('custom');
              }}
              className={`p-4 border-2 rounded-lg transition-all ${
                formData.platform === 'twitch'
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-purple-300'
              }`}
            >
              <div className="text-center">
                <div className="text-3xl mb-2">🎮</div>
                <div className="font-semibold">{t('widgets.twitch') || 'Twitch'}</div>
              </div>
            </button>
          </div>
        </div>

        {/* Channel ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.channelId')} *
          </label>
          <input
            type="text"
            value={formData.channelId}
            onChange={(e) => {
              setFormData({ ...formData, channelId: e.target.value });
              setSelectedPreset('custom');
            }}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
            placeholder={
              formData.platform === 'youtube' 
                ? '@channelname or video ID'
                : 'channelname (lowercase)'
            }
          />
          {errors.channelId && (
            <p className="text-red-500 text-sm mt-1">{errors.channelId}</p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {formData.platform === 'youtube'
              ? 'YouTube: Channel ID or @username (e.g., @TEDx)'
              : 'Twitch: Channel name in lowercase (e.g., ninja)'}
          </p>
        </div>

        {/* Theme Selection - FIXED */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.theme') || 'Design-Theme'}
          </label>
          <select
            value={formData.theme}
            onChange={(e) => {
              setFormData({ ...formData, theme: e.target.value });
              setSelectedPreset('custom');
            }}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="dark">{t('widgets.themeDark') || 'Dunkel'}</option>
            <option value="blue">{t('widgets.themeBlue') || 'Blau (AlpenScreen)'}</option>
            <option value="classicBlue">{t('widgets.themeClassicBlue') || 'Klassisch (Gradient Blau)'}</option>
            <option value="purple">{t('widgets.themePurple') || 'Lila (Twitch)'}</option>
            <option value="gaming">{t('widgets.themeGaming') || 'Gaming (Grün)'}</option>
            <option value="minimal">{t('widgets.themeMinimal') || 'Minimal (Schwarz)'}</option>
          </select>
        </div>

        {/* Playback Options - FIXED */}
        <div className="grid grid-cols-2 gap-4">
          <label className="flex items-center gap-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
            <input
              type="checkbox"
              checked={formData.autoplay}
              onChange={(e) => {
                setFormData({ ...formData, autoplay: e.target.checked });
                setSelectedPreset('custom');
              }}
              className="w-5 h-5 text-purple-500 rounded focus:ring-purple-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('widgets.autoplay') || 'Autoplay'}</span>
          </label>

          <label className="flex items-center gap-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
            <input
              type="checkbox"
              checked={formData.muted}
              onChange={(e) => {
                setFormData({ ...formData, muted: e.target.checked });
                setSelectedPreset('custom');
              }}
              className="w-5 h-5 text-purple-500 rounded focus:ring-purple-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('widgets.muted') || 'Stumm starten'}</span>
          </label>
        </div>

        {/* Show Chat (Twitch only) - FIXED */}
        {formData.platform === 'twitch' && (
          <label className="flex items-center gap-3 p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-purple-50 dark:bg-purple-900/10 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.showChat}
              onChange={(e) => {
                setFormData({ ...formData, showChat: e.target.checked });
                setSelectedPreset('custom');
              }}
              className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
            />
            <span className="text-gray-700 dark:text-gray-300 font-medium">
              {t('widgets.showChat') || 'Chat anzeigen'} (Twitch only)
            </span>
          </label>
        )}

        {/* Fallback Behavior - FIXED */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.fallbackBehavior') || 'Offline-Verhalten'}
          </label>
          <select
            value={formData.fallbackBehavior}
            onChange={(e) => {
              setFormData({ ...formData, fallbackBehavior: e.target.value });
              setSelectedPreset('custom');
            }}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="skip">{t('widgets.skipToNext') || 'Zum nächsten Element springen'}</option>
            <option value="show_message">{t('widgets.showOfflineMessage') || 'Offline-Nachricht anzeigen'}</option>
            <option value="show_logo">{t('widgets.showChannelLogo') || 'Kanal-Logo anzeigen'}</option>
          </select>
        </div>

        {/* Check Interval - FIXED */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.checkInterval') || 'Status-Prüfintervall (Sekunden)'}
          </label>
          <input
            type="number"
            min="60"
            value={formData.checkInterval}
            onChange={(e) => {
              setFormData({ ...formData, checkInterval: e.target.value });
              setSelectedPreset('custom');
            }}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
          />
          {errors.checkInterval && (
            <p className="text-red-500 text-sm mt-1">{errors.checkInterval}</p>
          )}
        </div>

        {/* ✅ VISUAL PREVIEW */}
        {formData.channelId && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Sparkles size={20} />
              {t('widgets.livePreview') || 'Live-Vorschau'}
            </h3>
            
            <div className="relative bg-black rounded-lg overflow-hidden" style={{ height: '300px' }}>
              {/* Live Indicator */}
              <div className="absolute top-4 right-4 z-10 bg-red-600 px-4 py-2 rounded-full flex items-center gap-2">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                <span className="font-bold text-white text-sm">LIVE</span>
              </div>

              {/* Platform Icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                {formData.platform === 'youtube' ? (
                  <div className="text-center">
                    <div className="text-8xl mb-4">▶️</div>
                    <div className="text-white text-2xl font-bold">{formData.channelId}</div>
                    <div className="text-gray-400 text-sm mt-2">YouTube Stream</div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-8xl mb-4">🎮</div>
                    <div className="text-white text-2xl font-bold">{formData.channelId}</div>
                    <div className="text-purple-400 text-sm mt-2">Twitch Stream</div>
                  </div>
                )}
              </div>

              {/* Channel Info Overlay */}
              <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-sm px-4 py-2 rounded-lg">
                <div className="flex items-center gap-2 text-white text-sm">
                  <Video size={16} />
                  <span className="capitalize">{formData.platform}</span>
                  <span className="opacity-50">•</span>
                  <span>{formData.channelId}</span>
                </div>
              </div>

              {/* Settings Info */}
              <div className="absolute top-4 left-4 flex gap-2">
                {formData.autoplay && (
                  <div className="bg-green-600 px-2 py-1 rounded text-white text-xs font-bold">
                    AUTOPLAY
                  </div>
                )}
                {formData.muted && (
                  <div className="bg-gray-600 px-2 py-1 rounded text-white text-xs font-bold">
                    MUTED
                  </div>
                )}
                {formData.showChat && (
                  <div className="bg-purple-600 px-2 py-1 rounded text-white text-xs font-bold">
                    + CHAT
                  </div>
                )}
              </div>
            </div>

            <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-2">
              Theme: <strong>{formData.theme}</strong> • Fallback: <strong>{formData.fallbackBehavior}</strong>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-colors font-semibold"
        >
          {existingConfig ? t('common.save') : t('common.create')}
        </button>
      </div>
    </div>
  );
};

export default StreamingWidgetConfig;